import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { creativesAPI } from '../creativesAPI';
import type {
  CreativeResult,
  CreativeFilterDef,
  CreativeQueryParams,
} from '@/entities/creatives';

export function useCreativeFolderNamesQuery() {
  return useQuery({
    queryKey: ['creatives', 'names'],
    queryFn: () => creativesAPI.getCreativeFolderNames(),
    staleTime: 30_000,
  });
}

export function useCreativesQuery(
  params: CreativeQueryParams = {},
  options?: UseQueryOptions<CreativeResult, unknown, CreativeResult, any[]>
) {
  const { filters = {}, search = '', page = 0, rowsPerPage = 10 } = params;

  const effectiveSearch = search.length > 2 ? search : '';
  const effectivePage = effectiveSearch ? 0 : page;

  return useQuery({
    queryKey: [
      'creatives',
      { filters, search: effectiveSearch, page: effectivePage, rowsPerPage },
    ],
    queryFn: () =>
      creativesAPI.fetchCreatives({
        filters,
        search: effectiveSearch,
        page: effectivePage,
        rowsPerPage,
      }),
    staleTime: 30_000, // 30 seconds
    ...options,
  });
}

export function useCreativeFiltersQuery(
  options?: UseQueryOptions<
    CreativeFilterDef[],
    unknown,
    CreativeFilterDef[],
    any[]
  >
) {
  return useQuery({
    queryKey: ['creativeFilters'],
    queryFn: () => creativesAPI.fetchCreativeFilters(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useUploadCreativeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => creativesAPI.uploadCreative(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creatives'] }),
  });
}

export function useDeleteCreativeMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => creativesAPI.deleteCreative(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creatives'] }),
  });
}

export function useEditCreativeAssetMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      brandId,
      body,
    }: {
      brandId: string;
      body: { action: 'ADD' | 'DELETE'; assetId: string[] | null; data: any };
    }) => creativesAPI.editCreativeAsset(brandId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creatives'] }),
  });
}

export function useScreenPlaylistQuery(
  deviceId: string | number | null,
  options?: UseQueryOptions<any, unknown, any, any[]>
) {
  return useQuery({
    queryKey: ['screenPlaylist', deviceId],
    queryFn: () => creativesAPI.getScreenPlaylist(deviceId!),
    enabled: !!deviceId, // Only run query if deviceId exists
    staleTime: 30_000, // 30 seconds
    ...options,
  });
}

export function useCreateScreenPlaylistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      selectedCreatives: any[];
      deviceId: string | number;
    }) => creativesAPI.createScreenPlaylist(payload),
    onSuccess: (_, variables) => {
      // Invalidate and refetch screen playlist
      queryClient.invalidateQueries({
        queryKey: ['screenPlaylist', variables.deviceId],
      });
    },
  });
}

export function useUpdateScreenPlaylistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      selectedCreatives: any[];
      playlistId: string;
      deviceId: string | number;
    }) => creativesAPI.updateScreenPlaylist(payload),
    onSuccess: (_, variables) => {
      // Invalidate and refetch screen playlist
      queryClient.invalidateQueries({
        queryKey: ['screenPlaylist', variables.deviceId],
      });
    },
  });
}

export function useUpdatePlaylistCreativesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      deviceId: string | number;
      creatives: Array<{
        id: string;
        slotIndex: number;
        priority: number;
        campaignId: string;
        creativeAssetId: string;
      }>;
    }) =>
      creativesAPI.updatePlaylistCreatives(payload.deviceId, {
        creatives: payload.creatives,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['screenPlaylist', variables.deviceId],
      });
    },
  });
}
